import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {
    Breadcrumb,
    ContextualMenuItemType,
    DefaultButton,
    DetailsListLayoutMode,
    Dropdown,
    FontWeights,
    IButtonStyles,
    IColumn,
    IComboBoxOption,
    IconButton,
    IContextualMenuProps,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"
import ApprovalService, {
    IDeleteApprovalActionRequest,
    IGetApprovalActionRequest,
    IGetApprovalActionResponse,
    IGetApprovalListMasterResponse,
    IGetApprovalStepResponse,
    IPostApprovalAllActionMapControlRequest
} from "../../services/Approval"
import CustomDialog from "../../share/CustomDialog"
import {
    ApprovalActionEdit,
    ApprovalActionResponseEdit,
    ApprovalEmailPreview,
    contentStyles,
    iconButtonStyles, ILookupValues
} from "./index"
import {useBranding} from "../../hooks/useBranding"
import {decode} from 'html-entities'

type Properties = {
    templateId: string
    setSelectedPivot: any
    documentOnlyAudienceId: string
    currentItem: IGetApprovalStepResponse | null
    steps: IGetApprovalStepResponse[],
    templateContentControls: IComboBoxOption[]
    mapContentControls: IComboBoxOption[]
    masterLists: IGetApprovalListMasterResponse[]
}

export function ApprovalSteps({
                                  currentItem,
                                  documentOnlyAudienceId,
                                  mapContentControls,
                                  masterLists,
                                  setSelectedPivot,
                                  steps,
                                  templateContentControls,
                                  templateId
                              }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    const iconListButtonStyles: Partial<IButtonStyles> = {root: {float: 'left', height: 'inherit'}}

    const [hasTimerMessageApprove, setHasTimerMessageApprove] = useState(false)
    const [isLoadedContent, setIsLoadedContent] = useState(false)
    const [isLoadingApproveActions, setIsLoadingApproveActions] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [messageApprove, setMessageApprove] = useState("")
    const [showApprovalActionModal, setShowApprovalActionModal] = useState(false)
    const [showApprovalResponseActionModal, setShowApprovalResponseActionModal] = useState(false)
    const [showDeleteAction, setShowDeleteAction] = useState(false)
    const [showChangeMapControl, setShowChangeMapControl] = useState(false)
    const [showChangeMapControlKey, setShowChangeMapControlKey] = useState<string | undefined>(undefined)
    const [showEmailPreview, setShowEmailPreview] = useState(false)
    const [showMessageApprove, setShowMessageApprove] = useState(false)
    const [statusMessageApprove, setStatusMessageApprove] = useState<StatusEnum>(StatusEnum.Info)
    const [actions, setActions] = useState<IGetApprovalActionResponse[]>([])
    const [currentAction, setCurrentAction] = useState<IGetApprovalActionResponse | null>(null)
    const [mapContentControlKey, setMapContentControlKey] = useState<string | undefined>(undefined)
    const instructionItems: any = t('text.instructionStepsItem', {ns: 'approval', returnObjects: true})
    const actionOrder = ['Approve', 'Complete', 'Reject', 'Abort']


    /**
     * Determines the color type style based on the given type.
     *
     * @param {string} type - The type of the color, e.g., 'Approve', 'Complete', or 'Reject'.
     * @return {string} The corresponding style for the specified color type.
     */
    function ColorType(type: string): string {
        switch (type.toLowerCase()) {
            case 'approve':
                return contentStyles(branding.theme).typeApprove
            case 'complete':
                return contentStyles(branding.theme).typeComplete
            case 'reject':
                return contentStyles(branding.theme).typeReject
            case 'abort':
                return contentStyles(branding.theme).typeAbort
            default:
                return contentStyles(branding.theme).typeReject
        }
    }

    const menuApprovalStepsProps = (item: IGetApprovalActionResponse): IContextualMenuProps => {

        return {
            items: [
                {
                    key: 'edit',
                    text: t("Common.MenuAction.Edit", {ns: "common"}),
                    title: t("Common.MenuAction.Edit", {ns: "common"}),
                    iconProps: {iconName: 'Edit'},
                    onClick: () => {
                        setCurrentAction(item)
                        setShowApprovalActionModal(true)
                    }
                },
                {
                    key: 'Divider1',
                    itemType: ContextualMenuItemType.Divider
                },
                {
                    key: 'editResponseAction',
                    text: t("title.editResponseAction", {ns: "approval"}),
                    title: t("title.editResponseAction", {ns: "approval"}),
                    iconProps: {iconName: 'FeedbackResponseSolid'},
                    onClick: () => {
                        ShowResponseActions(item)
                    }
                },
                {
                    key: 'Divider2',
                    itemType: ContextualMenuItemType.Divider
                },
                {
                    key: 'delete',
                    text: t("Common.MenuAction.Delete", {ns: "common"}),
                    title: t("Common.MenuAction.Delete", {ns: "common"}),
                    iconProps: {iconName: 'Trash'},
                    onClick: () => {
                        setCurrentAction(item)
                        setShowDeleteAction(true)
                    }
                }

            ],
            directionalHintFixed: true,
        }
    }


    const approvalActionsColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.type', {ns: 'approval'}),
            fieldName: 'Type',
            minWidth: 80,
            maxWidth: 80,
            isMultiline: false,
            isResizable: false,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.type', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.type', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalActionResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            height: "100%"
                        }
                    }}>
                        <Text variant={'medium'} title={item.Type} className={ColorType(item.Type)}>
                            {item.Type}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: t('title.emailAddresses', {ns: 'approval'}),
            fieldName: 'EmailAddressLookupRule',
            minWidth: 140,
            isMultiline: false,
            isResizable: true,
            isCollapsible: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.emailAddresses', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.emailAddresses', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalActionResponse) => {
                let existList = false
                try {
                    const lookupRuleArray = JSON.parse(item.EmailAddressLookupRule) as ILookupValues[]
                    const currentList = lookupRuleArray.find(item => item.Name.toLowerCase().trim() === "looklist")

                    if (currentList) {
                        const currentListItem = masterLists.find(item => item.Id  === Number(currentList.Value))
                        if (currentListItem) {
                            existList = true
                        }
                        else {
                            item.EmailAddressLookupRule = ''
                        }
                    }

                } catch (_) {

                }

                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            whiteSpace: 'break-spaces'
                        }
                    }}>

                        {item.Type === 'Approve' ?
                            <>
                                <Text variant={'medium'} block styles={{root: {paddingBottom: 8}}}>

                                    {item.EmailAddress !== '' ? item.EmailAddress : ''}

                                    {item.EmailAddressLookupRule !== '' && existList ? `, ${t("title.lookupRule", {ns: 'approval'})}` : ''}

                                    {item.AdditionalNotifys.trim().length > 0 &&
                                        <strong>{t("text.others", {ns: 'approval'})}</strong>
                                    }
                                </Text>
                            </>
                            :
                            <>
                                <Text variant={'medium'} block styles={{root: {paddingBottom: 8}}}>
                                    {item.AdditionalNotifys.trim().length > 0 &&
                                        <>{t("text.othersColumn", {ns: 'approval'})}</>
                                    }
                                </Text>
                            </>
                        }


                        <DefaultButton
                            title={t('Common.Button.PreviewEmail', {ns: 'common'})}
                            text={t('Common.Button.PreviewEmail', {ns: 'common'})}
                            disabled={isSaving || isDeleting}
                            onClick={() => ShowEmailPreview(item)}
                        />

                    </Stack>
                </>

            }
        },
        {
            key: 'column3',
            name: t('title.mapControl', {ns: 'approval'}),
            fieldName: 'MapControls',
            minWidth: 200,
            maxWidth: 200,
            isMultiline: false,
            isResizable: true,
            onRenderHeader: () => {
                return <>
                    {actions.length > 0 ?

                        <Dropdown
                            disabled={isSaving || isLoadingApproveActions || isDeleting}
                            selectedKey={mapContentControlKey}
                            label={''}

                            placeHolder={t('title.mapControl', {ns: 'approval'})}
                            options={mapContentControls}
                            styles={{
                                root: {
                                    width: 200
                                }
                            }}
                            onChange={(_, option) => {
                                if (option?.key !== undefined && option?.key !== mapContentControlKey) {
                                    setShowChangeMapControl(true)
                                    setShowChangeMapControlKey(String(option?.key))
                                }
                            }}

                        />
                        :
                        <Text variant={'medium'} title={t('title.mapControl', {ns: 'approval'})}
                              styles={{root: {fontWeight: FontWeights.semibold}}}>
                            {t('title.mapControl', {ns: 'approval'})}
                        </Text>
                    }
                </>
            },
            onRender: (item: IGetApprovalActionResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            height: "100%"
                        }
                    }}>
                        <Text variant={'medium'} title={item.MapControls}
                              className={contentStyles(branding.theme).contentControls}>
                            {item.MapControls}
                        </Text>
                    </Stack>
                </>
            }

        },
        {
            key: 'column5',
            name: t('title.actions', {ns: 'approval'}),
            fieldName: 'Actions',
            minWidth: 50,
            maxWidth: 50,
            isMultiline: false,
            isResizable: false,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.actions', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.actions', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalActionResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%"
                        }
                    }}>

                        <IconButton
                            menuIconProps={{iconName: 'MoreVertical', style: {fontSize: 20, color: '#242424'}}}
                            role="button"
                            title={t('label.colShowActions', {ns: 'common'})}
                            data={item}
                            disabled={isSaving || isLoadingApproveActions || isDeleting}
                            aria-haspopup
                            aria-label={t('label.colShowActions', {ns: 'common'})}
                            styles={iconListButtonStyles}
                            menuProps={menuApprovalStepsProps(item)}
                        />
                    </Stack>
                </>

            }
        }
    ]


    /**
     * Displays the email preview based on the provided approval action response data.
     *
     * @param {IGetApprovalActionResponse} item - The approval action response object containing information needed to render the email preview.
     * @return {void} This method does not return a value.
     */
    function ShowEmailPreview(item: IGetApprovalActionResponse): void {
        setCurrentAction(item)
        setShowEmailPreview(true)
    }

    /**
     * Displays the response actions for the given approval action response item.
     * @param item
     * @constructor
     */
    function ShowResponseActions(item: IGetApprovalActionResponse): void {
        setCurrentAction(item)
        setShowApprovalResponseActionModal(true)
    }


    /**
     * Callback function to handle the dismissal of the email preview.
     * It resets the current action state and hides the email preview modal.
     */
    const onDismissPreviewEmail = () => {
        setCurrentAction(null)
        setShowEmailPreview(false)
    }


    /**
     * Handles the dismissal of the Edit Modal component.
     *
     * This function is typically invoked to close the Edit Modal and perform
     * necessary cleanup or updates. By default, the function does not trigger
     * a reload of actions unless the `update` parameter is set to true.
     *
     * @param {boolean} [update=false] - Determines whether the action list should
     *                                   be reloaded upon dismissing the modal. Defaults to false.
     */
    const OnDismissEditModal = (update: boolean = false) => {
        setCurrentAction(null)
        setShowApprovalActionModal(false)
        if (update) {
            loadActions(true).then()
        }
    }

    const OnDismissResponseActionEditModal = (update: boolean = false) => {
        setCurrentAction(null)
        setShowApprovalResponseActionModal(false)
        if (update) {
            loadActions(true).then()
        }
    }


    /**
     * Load Template Context
     * @param clearMessages
     */
    const loadActions = async (clearMessages: boolean = false) => {

        if (clearMessages) {
            ClearMessageApprove()
        }

        setIsLoadedContent(false)
        setIsLoadingApproveActions(true)

        const request: IGetApprovalActionRequest = {
            TemplateId: templateId,
            Mode: 'approvalStepId',
            Id: String(currentItem?.Id ?? '0')
        }

        await ApprovalService.getApprovalAction(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalActionResponse>

                    if (Array.isArray(resultData)) {

                        setIsLoadedContent(!(
                            resultData.some(item => item.Type === 'Approve') &&
                            resultData.some(item => item.Type === 'Complete') &&
                            resultData.some(item => item.Type === 'Reject') &&
                            resultData.some(item => item.Type === 'Abort')
                        ))

                        const sortedData = resultData.sort((a, b) => {
                            return actionOrder.indexOf(a.Type) - actionOrder.indexOf(b.Type);
                        })

                        setActions(sortedData)
                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }

            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingApproveActions(false)
            })


    }

    useEffect(() => {
        loadActions(true).then()
    }, [])


    useEffect(() => {
        setMapContentControlKey(actions.length > 0 ? actions[0].MapControls : undefined)
    }, [actions])


    const changeResponseControl = async () => {
        setShowChangeMapControl(false)
        setIsSaving(true)

        const request: IPostApprovalAllActionMapControlRequest = {
            ApprovalStepId: currentItem?.Id ?? 0,
            MapControls: showChangeMapControlKey ?? '',
            TemplateId: templateId,
            Mode: 'updatemapcontrol'
        }

        await ApprovalService.postApprovalAction(request)
            .then(data => {
                if (data.Success) {

                    setMapContentControlKey(showChangeMapControlKey)
                    loadActions(true).then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsSaving(false)
            })

    }

    /**
     * Delete
     */
    const deleteAction = async () => {
        ClearMessageApprove()
        setIsDeleting(true)
        setShowDeleteAction(false)

        const requestKim: IDeleteApprovalActionRequest = {
            Id: currentAction?.Id.toString() ?? '0',
            TemplateId: templateId
        }
        await ApprovalService.deleteApprovalAction(requestKim)
            .then(data => {
                if (data.Success) {
                    ShowMessageApprove(t('message.successRemoved', {ns: 'common'}), StatusEnum.Success, true)
                    loadActions(false).then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsDeleting(false)
            })
    }

    /**
     * Displays an approval message with a specified status.
     *
     * @param {string} message - The message content to be displayed.
     * @param {StatusEnum} status - The status associated with the approval message.
     * @param hasTimer
     * @return {void} This function does not return a value.
     */

    function ShowMessageApprove(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessageApprove(decode(message))
        setStatusMessageApprove(status)
        setHasTimerMessageApprove(hasTimer)
        setShowMessageApprove(true)
    }

    /**
     * Clears the message approval status by resetting the message content and hiding the approval notification.
     *
     * @return {void} This function does not return any value.
     */

    function ClearMessageApprove(): void {
        setMessageApprove('')
        setShowMessageApprove(false)
        setHasTimerMessageApprove(false)
    }

    /**
     * Initiates the process of adding a new action by resetting the current item
     * and displaying the approval step modal.
     *
     * @return {void} Does not return a value.
     */

    function AddNewAction(): void {
        setCurrentAction(null)
        setShowApprovalActionModal(true)
    }

    /**
     * Resets the selection to the 'process' pivot.
     * This function is used to update or reset the selected pivot value.
     *
     * @return {void} Does not return any value.
     */

    function Back(): void {
        setSelectedPivot('process')
    }

    return (
        <>

            <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                   className="scrollVisible">


                {showMessageApprove &&
                    <>
                        <Stack styles={{root: {paddingTop: 8}}}>
                            <StatusMessage status={statusMessageApprove} hasTimer={hasTimerMessageApprove}
                                           truncated={true}
                                           isMultiline={true}
                                           dismiss={true}

                                           setShowMessage={setShowMessageApprove}>
                                <div>{messageApprove}</div>
                            </StatusMessage>
                        </Stack>
                    </>
                }

                <Stack horizontal styles={{root: {alignItems: 'center'}}}>
                    <Stack>
                        <IconButton
                            styles={iconButtonStyles}
                            iconProps={{iconName: 'Back'}}
                            ariaLabel={t('Common.Button.Back', {ns: 'common'})}
                            onClick={Back}
                            title={t('Common.Button.Back', {ns: 'common'})}
                        />

                    </Stack>
                    <Stack grow={1}>
                        <Breadcrumb
                            items={[
                                {
                                    text: t('title.section', {ns: 'approval'}),
                                    key: 'approval',
                                    disabled: isSaving || isLoadingApproveActions || isDeleting,
                                    onClick: Back
                                },
                                {
                                    text: t('title.sectionSteps', {
                                        ns: 'approval',
                                        StepNumber: currentItem?.Order,
                                        StepName: currentItem?.Name
                                    }),
                                    key: 'steps',

                                }
                            ]}
                            styles={{root: {marginTop: 0}, item: {':first-child': {paddingLeft: 0}}}}
                            maxDisplayedItems={10}
                            overflowAriaLabel={t("Common.Button.See more", {ns: 'common'})}
                        />
                    </Stack>
                </Stack>


                <Text variant={'medium'} styles={{root: {paddingTop: 8, paddingBottom: 8}}}>
                    <Trans>
                        {t('text.instructionSteps', {ns: 'approval'})}
                    </Trans>

                    <Trans>
                        <ol>
                            {instructionItems.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ol>
                    </Trans>

                </Text>

                <Separator/>
                <Stack horizontal styles={{root: {alignItems: "center"}}} tokens={{childrenGap: 8}}>
                    <StackItem grow={1}>
                        <Text variant={'mediumPlus'}
                              styles={{root: {fontWeight: FontWeights.semibold}}}>
                            {t('title.approvalActions', {ns: 'approval'})}
                        </Text>
                    </StackItem>
                    <StackItem>
                        <DefaultButton
                            title={t('text.addAction', {ns: 'approval'})}
                            text={t('text.addAction', {ns: 'approval'})}
                            disabled={!isLoadedContent || isSaving || isLoadingApproveActions || isDeleting}
                            onClick={AddNewAction}
                        />
                    </StackItem>
                </Stack>

                <ShimmeredDetailsList
                    items={actions}
                    enableShimmer={isLoadingApproveActions}
                    compact={false}
                    columns={approvalActionsColumns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    isHeaderVisible={true}
                    isSelectedOnFocus={false}
                    setKey={"Id"}
                />

                {actions.length === 0 && !isLoadingApproveActions &&
                    <>
                        <p style={{textAlign: "center"}}>
                            <Text variant={'small'}>
                                <Trans>
                                    {t('text.emptyActions', {ns: 'approval'})}
                                </Trans>
                            </Text>
                        </p>
                    </>
                }

            </Stack>


            {/*-- START FOOTER --*/}
            <Separator></Separator>
            <Stack horizontal tokens={{childrenGap: 8}}
                   styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                <Stack grow={1}>&nbsp;</Stack>
                {isLoadingApproveActions &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinner', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }
                {isSaving &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinnerSaving', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }

                {isDeleting &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinnerDeleting', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }

                <Stack horizontal tokens={{childrenGap: 8}} wrap={false}>

                    <DefaultButton
                        disabled={isDeleting || isSaving || isLoadingApproveActions}
                        onClick={Back} iconProps={{iconName: 'Back'}}
                        text={t('Common.Button.Back', {ns: 'common'})}
                        title={t('Common.Button.Back', {ns: 'common'})}/>

                </Stack>
            </Stack>

            <CustomDialog title={t('title.deleteAction', {ns: 'approval', Name: currentAction?.Type ?? ''})}
                          titleId={`removeAction${templateId}`}
                          actionText={t('Common.Button.Delete', {ns: 'common'})}
                          confirm={deleteAction}
                          close={() => {
                              setShowDeleteAction(false)
                          }}
                          isOpen={showDeleteAction}
            >
                <Trans>
                    {t('text.confirmRemoveAction', {ns: 'approval'})}
                </Trans>
            </CustomDialog>


            <CustomDialog title={t('title.changeMapControl', {ns: 'approval', Name: currentAction?.Type ?? ''})}
                          titleId={`changeResponseControl${templateId}`}
                          actionText={t('Common.Button.Change', {ns: 'common'})}
                          confirm={changeResponseControl}
                          close={() => {
                              setShowChangeMapControl(false)
                          }}
                          isOpen={showChangeMapControl}
            >
                <Trans>
                    {t('text.confirmMapResponseControl', {ns: 'approval'})}
                </Trans>
            </CustomDialog>


            {showEmailPreview &&
                <ApprovalEmailPreview showModal={showEmailPreview} action={currentAction}
                                      onDismissModal={onDismissPreviewEmail}/>
            }

            {showApprovalActionModal &&
                <ApprovalActionEdit
                    masterLists={masterLists}
                    actions={actions}
                    showModal={showApprovalActionModal}
                    templateContentControls={templateContentControls}
                    mapContentControls={mapContentControls}
                    templateId={templateId}
                    documentOnlyAudienceId={documentOnlyAudienceId}
                    action={currentAction}
                    currentStep={currentItem}
                    onDismissModal={OnDismissEditModal}/>
            }

            {showApprovalResponseActionModal &&
                <ApprovalActionResponseEdit
                    showModal={showApprovalResponseActionModal}
                    templateId={templateId}
                    action={currentAction}
                    steps={steps}
                    currentStep={currentItem}
                    onDismissModal={OnDismissResponseActionEditModal}/>
            }


        </>
    )
}
